import type {
  AssignPassengerInput,
  ManifestExportFormat,
  ManifestExportQueuedDto,
  ManifestExportStatusDto,
  TripPassengerDto,
} from "@tc/shared";
import { ApiError } from "@/features/trips/api";

const API_URL = import.meta.env.VITE_API_URL ?? "/api";

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    headers: { accept: "application/json", "content-type": "application/json" },
    ...init,
  });
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = (await res.json()) as { message?: string | string[] };
      if (body?.message)
        message = Array.isArray(body.message) ? body.message.join(", ") : body.message;
    } catch {
      /* non-JSON error body — keep the status message */
    }
    throw new ApiError(message, res.status);
  }
  return (res.status === 204 ? undefined : await res.json()) as T;
}

/**
 * Trip manifest calls (TW-3448) — the `trip_passenger` assignments backing the shared builder.
 * The manifest is co-edited: both the club and back office read + write the same list (RLS scopes
 * each to the trips it may see). Assign / unassign / add-all return the full manifest so the
 * builder re-syncs in one round-trip; submit is the club-only notify-to-TC action.
 */
export const manifestApi = {
  list: (tripId: string) => req<TripPassengerDto[]>(`/trips/${tripId}/passengers`),
  assign: (tripId: string, body: AssignPassengerInput) =>
    req<TripPassengerDto[]>(`/trips/${tripId}/passengers`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  unassign: (tripId: string, passengerId: string) =>
    req<TripPassengerDto[]>(`/trips/${tripId}/passengers/${passengerId}`, { method: "DELETE" }),
  addAllPlayers: (tripId: string) =>
    req<TripPassengerDto[]>(`/trips/${tripId}/passengers/add-all-players`, { method: "POST" }),
  submit: (tripId: string) =>
    req<{ ok: true; assignedCount: number; submittedAt: string }>(
      `/trips/${tripId}/passengers/submit`,
      { method: "POST" },
    ),

  /** Queue a manifest export (CSV/PDF) in the worker; returns the id to poll (TW-3449). */
  requestExport: (tripId: string, format: ManifestExportFormat) =>
    req<ManifestExportQueuedDto>(`/trips/${tripId}/passengers/export`, {
      method: "POST",
      body: JSON.stringify({ format }),
    }),
  /** Poll a queued export — `pending` until the worker has written it, then `ready` + signed URL. */
  exportStatus: (tripId: string, exportId: string) =>
    req<ManifestExportStatusDto>(`/trips/${tripId}/passengers/export/${exportId}`),
};

/**
 * Request a manifest export and poll until the worker has produced it, then resolve the signed
 * download URL + filename. Polls a bounded number of times so a stuck job surfaces as an error
 * rather than spinning forever (the export is small — generation is near-instant in practice).
 */
export async function runManifestExport(
  tripId: string,
  format: ManifestExportFormat,
): Promise<{ url: string; filename: string }> {
  const { exportId } = await manifestApi.requestExport(tripId, format);
  for (let attempt = 0; attempt < 30; attempt += 1) {
    const status = await manifestApi.exportStatus(tripId, exportId);
    if (status.status === "ready" && status.downloadUrl) {
      return { url: status.downloadUrl, filename: status.document?.filename ?? `manifest.${format}` };
    }
    await new Promise((resolve) => setTimeout(resolve, 600));
  }
  throw new ApiError("Export timed out", 504);
}
